import { Box, Link, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { fullDateTime, shortDateTime } from '../../shared/dateTime';
import { plNumber } from '../../shared/number';
import type { WorkReserve } from './suggestionsApi';
import { placeText, type PositionPlace } from './geoPoint';
import { useFeature } from '../featureFlags/FeatureGate';
import {
  FEATURE_DETAILS,
  FEATURE_FEASIBILITY,
  SortableLabel,
  WINDOW_RESERVE_TOOLTIP,
  type RecommendationSort,
  type RecommendationSortKey,
} from './recommendationView';

/**
 * Kolumny wspólne dla obu kierunków podpowiedzi — zlecenie → auta
 * (`VehicleSuggestions`) i auto → zlecenia (`fleet/CarOrderSuggestions`).
 * Jedno miejsce, żeby obie listy mówiły to samo (omówienie 01.10.2026).
 */

const NOWRAP = { whiteSpace: 'nowrap' } as const;

/** Pozycja z GPS — `POSITION_SOURCE.TELEMATICS` w API. */
const GPS_SOURCE = 'TELEMATYKA';

/** Pozycja auta z odpowiedzi podpowiedzi — tyle, ile potrzeba kolumnie „Wolny od”. */
export interface FreeFromPosition {
  freeFrom: string;
  /** `TELEMATYKA` (GPS) albo `HISTORIA_ZLECEN`. */
  zrodlo: string;
  /** Numer zlecenia, po którym auto się zwalnia. */
  fromOrder: string | null;
  /** Id tego zlecenia — do linku. Brak = starsze API. */
  fromOrderId?: number | null;
  /** Czas odczytu GPS (ISO); `null` dla pozycji z historii. */
  measuredAt?: string | null;
  /**
   * Miejscowość w pobliżu odczytu GPS (geokodowanie odwrotne, 06.10.2026);
   * brak = starsze API, wyłączone geokodowanie albo HERE jeszcze nie odpowiedział.
   */
  place?: PositionPlace | null;
  /**
   * Adres pozycji z API — przy historii rozładunek zlecenia, po którym auto
   * się zwalnia (miasto pokazujemy od 15.10.2026, `recommendationDetails`).
   */
  adres?: { country: string | null; postalCode?: string | null; city: string | null } | null;
}

/**
 * Miasto rozładunku przy pozycji z historii („DE BERLIN”) — `null` przy GPS
 * (tam jest miejscowość z geokodowania) i bez miasta w adresie.
 */
export function historyPlaceText(p: FreeFromPosition): string | null {
  if (p.zrodlo === GPS_SOURCE || !p.adres?.city) return null;
  return [p.adres.country, p.adres.city].filter(Boolean).join(' ');
}

/** Karta zlecenia w routerze aplikacji — react-admin chodzi na hashu, bez przedrostka. */
export const orderCardHref = (id: number) => `#/user-offers/${id}/show`;

/**
 * Samo źródło, bez dopisków: „GPS 01.10, 05:08” albo numer zlecenia jako
 * link otwierany w nowej karcie (planista nie traci listy podpowiedzi).
 */
function FreeFromSource({ position: p }: { position: FreeFromPosition }) {
  if (p.zrodlo === GPS_SOURCE) {
    // „GPS 02.10, 08:22 · okolice: Poznań (PL)” — miejscowość, gdy ją znamy.
    const place = placeText(p.place);
    if (!place) return <>{`GPS ${shortDateTime(p.measuredAt)}`}</>;
    // Miejscowość w drugiej linii, gdy kolumna wąska (06.10.2026: w jednej
    // linii poszerzała „Wolny od” i tabela rekomendacji przy oknie 1366 px
    // przewijała się w poziomie); czas odczytu zostaje w jednym kawałku.
    return (
      <>
        <span style={NOWRAP}>{`GPS ${shortDateTime(p.measuredAt)} ·`}</span>{' '}
        <span data-testid="free-from-place">{place}</span>
      </>
    );
  }
  const id = p.fromOrderId ?? null;
  const label = p.fromOrder ?? (id === null ? null : `#${id}`);
  if (label === null) return <>z historii zleceń</>;
  // Starsza odpowiedź API bez id — numer bez linku, zamiast linku donikąd.
  if (id === null) return <>{label}</>;
  return (
    <Link href={orderCardHref(id)} target="_blank" rel="noopener noreferrer" underline="hover">
      {label}
    </Link>
  );
}

/**
 * „Wolny od” (omówienie 01.10.2026): w pierwszej linii data i godzina,
 * w drugiej tylko źródło — czas odczytu GPS albo numer zlecenia, po którym
 * auto się zwalnia, jako link do karty zlecenia w nowej karcie przeglądarki.
 * Bez okienek z podglądem zlecenia — ustalenie ze spotkania.
 */
export function FreeFromCell({ position }: { position?: FreeFromPosition | null }) {
  // Miasto przy pozycji z historii (makieta FTL, Etap 1 — 15.10.2026).
  const details = useFeature(FEATURE_DETAILS);
  if (!position) return <>—</>;
  const historyPlace = details ? historyPlaceText(position) : null;
  // Z miejscowością źródło może zejść do drugiej linii (`FreeFromSource`).
  const wraps = position.zrodlo === GPS_SOURCE && placeText(position.place) !== null;
  return (
    <>
      <Tooltip title={fullDateTime(position.freeFrom)}>
        <Typography variant="body2" sx={NOWRAP}>
          {shortDateTime(position.freeFrom)}
        </Typography>
      </Tooltip>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={wraps ? { display: 'block' } : { display: 'block', ...NOWRAP }}
        data-testid="free-from-source"
      >
        <FreeFromSource position={position} />
      </Typography>
      {historyPlace && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block' }}
          data-testid="free-from-history-place"
        >
          {`rozładunek: ${historyPlace}`}
        </Typography>
      )}
    </>
  );
}

/**
 * „Zapas” w obu kierunkach rekomendacji (06.10.2026; omówienie z PM
 * 01.10.2026, §3.3): ile czasu pracy zostanie kierowcy po wykonaniu
 * zlecenia, jeśli wyślemy go teraz. Liczone tym samym zegarem co
 * „Rozładunek (szacunek)” (`user-offers/delivery-verdict.ts`,
 * `workReserve`) — w rekomendacjach pojazdów, w wariantach i w zleceniach
 * dla pojazdu (`fleet/CarOrderSuggestions`) ta sama komórka i ten sam opis.
 */
export const WORK_RESERVE_TOOLTIP =
  'Zapas czasu pracy kierowcy po wykonaniu zlecenia: ile zostanie mu po dolocie, załadunku, przejeździe i rozładunku, jeśli wyślemy go teraz. Czas załadunku i rozładunku to mediana postojów w tym miejscu (ślad GPS), gdy jest miarodajna — dość postojów i umiarkowany rozrzut; inaczej mediana floty ok. 2,2 h i 1,9 h. Najmniejsze z: jazdy do limitu dobowego (9 h), dnia pracy (13 h od początku) i limitu tygodniowego, bez ulg. Gdy po drodze wypada odpoczynek dobowy, zapas dotyczy dnia rozładunku. Szacunek.';

/**
 * Opis z 08.10.2026 — czasy według miejsca bez oceny miarodajności
 * (przełącznik `stopTimesReliablePlace` wyłączony, 19.10.2026).
 */
export const PLACE_WORK_RESERVE_TOOLTIP =
  'Zapas czasu pracy kierowcy po wykonaniu zlecenia: ile zostanie mu po dolocie, załadunku, przejeździe i rozładunku, jeśli wyślemy go teraz. Czas załadunku i rozładunku to mediana postojów w tym miejscu (ślad GPS), a bez danych — wartość domyślna ok. 2,2 h i 1,9 h. Najmniejsze z: jazdy do limitu dobowego (9 h), dnia pracy (13 h od początku) i limitu tygodniowego, bez ulg. Gdy po drodze wypada odpoczynek dobowy, zapas dotyczy dnia rozładunku. Szacunek.';

/**
 * Opis z 0.31.3 — wersja „obecna” (przełącznik `stopTimesByPlace`
 * wyłączony, 12.10.2026): stałe 2,2 h i 1,9 h.
 */
export const LEGACY_WORK_RESERVE_TOOLTIP =
  'Zapas czasu pracy kierowcy po wykonaniu zlecenia: ile zostanie mu po dolocie, załadunku (ok. 2,2 h), przejeździe i rozładunku (ok. 1,9 h), jeśli wyślemy go teraz. Najmniejsze z: jazdy do limitu dobowego (9 h), dnia pracy (13 h od początku) i limitu tygodniowego, bez ulg. Gdy po drodze wypada odpoczynek dobowy, zapas dotyczy dnia rozładunku. Szacunek.';

/**
 * Opis „Zapasu” według obowiązującej reguły czasu w punktach: stałe
 * (0.31.3), miejsce (08.10) albo tylko miarodajne miejsca (19.10.2026,
 * nakładka `stopTimesReliablePlace` na `stopTimesByPlace`).
 */
export function useWorkReserveTooltip(): string {
  const byPlace = useFeature('stopTimesByPlace');
  const reliable = useFeature('stopTimesReliablePlace');
  if (!byPlace) return LEGACY_WORK_RESERVE_TOOLTIP;
  return reliable ? WORK_RESERVE_TOOLTIP : PLACE_WORK_RESERVE_TOOLTIP;
}

/** Nagłówek „Zapas” z ikonką i opisem, jak liczony. */
export function WorkReserveHeader() {
  const tooltip = useWorkReserveTooltip();
  // Obok „Zapasu do okna” (15.10.2026) pełna nazwa — dwie różne liczby.
  const named = useFeature(FEATURE_FEASIBILITY);
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, ...(named ? {} : NOWRAP) }}
    >
      {named ? 'Zapas czasu pracy' : 'Zapas'}
      {/* `role="img"` z nazwą z podpowiedzi (Tooltip daje `aria-label`) —
          sama ikona jest ukryta przed czytnikiem ekranu. */}
      <Tooltip title={tooltip}>
        <Box
          component="span"
          role="img"
          tabIndex={0}
          sx={{ display: 'inline-flex', cursor: 'help', color: 'text.secondary' }}
        >
          <InfoOutlinedIcon fontSize="inherit" />
        </Box>
      </Tooltip>
    </Box>
  );
}

/**
 * Nagłówek kolumny dwóch zapasów — „Zapas do okna” nad „Zapasem czasu
 * pracy” (przełącznik `recommendationFeasibility`), w obu kierunkach
 * rekomendacji.
 *
 * Przegląd Etapu 1 w przeglądarce (15.10.2026): pełne nazwy z ikonkami
 * w kolumnie szerokości liczb łamały się na sześć linii (nagłówek tabeli
 * 144 px wysokości przy 1366 px). Teraz trzy krótkie linie bez łamania:
 * „Zapas”, pod nim „do okna” i „pracy” (podpis jak „koszt dolotu · marża”
 * pod „Dolotem”) — każdy z sortowaniem, a pełna nazwa i opis, jak liczony,
 * w dymku podpisu (zamiast ikonki ⓘ). Najszerszy wiersz („do okna” ze
 * strzałką sortowania) jest węższy niż dotychczasowy, więc kolumna się nie
 * poszerza — tabele mieszczą się przy 1366 px (decyzja 08.10.2026).
 */
export function ReserveColumnHeader({
  sort,
  onSort,
}: {
  sort?: RecommendationSort | null;
  onSort?: (key: RecommendationSortKey) => void;
}) {
  const tooltip = useWorkReserveTooltip();
  return (
    <Box sx={NOWRAP}>
      {/* Odstęp strzałki sortowania (18 px + marginesy) — „Zapas” stoi
          równo z podpisami pod nim, a nie nad strzałkami. */}
      <Box component="span" sx={{ display: 'block', mr: onSort ? '26px' : 0 }}>
        Zapas
      </Box>
      <Box data-testid="window-reserve-header">
        <SortableLabel sortKey="windowReserve" sort={sort} onSort={onSort} hint={WINDOW_RESERVE_TOOLTIP}>
          do okna
        </SortableLabel>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} data-testid="work-reserve-header">
        <SortableLabel
          sortKey="workReserve"
          sort={sort}
          onSort={onSort}
          hint={tooltip}
        >
          pracy
        </SortableLabel>
      </Typography>
    </Box>
  );
}

const LIMIT_LABELS: Record<NonNullable<WorkReserve['limitedBy']>, string> = {
  driving: 'limit jazdy dobowej',
  duty: 'koniec dnia pracy (13 h)',
  week: 'limit jazdy tygodniowej',
};

/** Rachunek zapasu zdaniami — do dymka nad liczbą. */
export function workReserveDetails(r: WorkReserve): string {
  const hours = (h: number) => `${plNumber(Math.max(0, h))} h`;
  const left = [
    r.drivingLeftH !== null ? `jazdy ${hours(r.drivingLeftH)}` : null,
    r.dutyLeftH !== null ? `dnia pracy ${hours(r.dutyLeftH)}` : null,
    r.weekLeftH !== null ? `do limitu tygodniowego ${hours(r.weekLeftH)}` : null,
  ].filter((x): x is string => x !== null);
  const parts = [
    r.finishedAt ? `Koniec rozładunku ok. ${shortDateTime(r.finishedAt)}.` : null,
    left.length > 0 ? `Po rozładunku zostaje: ${left.join(', ')}.` : null,
    r.limitedBy ? `Wyznacza: ${LIMIT_LABELS[r.limitedBy]}.` : null,
    r.restOnTheWay ? 'Po drodze odpoczynek dobowy — zapas dotyczy dnia rozładunku.' : null,
    r.assumptions.length > 0 ? `Założenia: ${r.assumptions.join('; ')}.` : null,
  ];
  return parts.filter(Boolean).join(' ');
}

/**
 * Komórka „Zapas”: liczba godzin z dymkiem, jak liczona. Bez liczby — „—”
 * z powodem w dymku (brak danych to nie zero). Starsze API bez pola — „—”.
 */
export function WorkReserveCell({ reserve }: { reserve?: WorkReserve | null }) {
  if (!reserve) return <>—</>;
  if (reserve.hours === null) {
    return (
      <Tooltip title={`Nie liczymy: ${reserve.reason ?? 'brak danych'}.`}>
        <Typography variant="body2" component="span" data-testid="work-reserve-empty">
          —
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={workReserveDetails(reserve)}>
      <Box component="span" sx={{ display: 'inline-block' }}>
        <Typography
          variant="body2"
          sx={NOWRAP}
          color={reserve.hours <= 0 ? 'warning.main' : undefined}
          data-testid="work-reserve-hours"
        >
          ok. {plNumber(reserve.hours)} h
        </Typography>
        {reserve.restOnTheWay && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ...NOWRAP }}>
            po odpoczynku
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}
